import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, ShieldCheck, ArrowLeftRight, UserCog, CheckCheck, ChevronRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, EmptyState } from "../components/common";

const ICONS = { transaction: ArrowLeftRight, security: ShieldCheck, account: UserCog };
const FALLBACK_LINK = { security: "/app/security", transaction: "/app/activity", account: "/app/profile" };

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((x) => x.map((n) => ({ ...n, read: true })));
  };

  const openNotification = async (n) => {
    if (!n.read) {
      setItems((x) => x.map((i) => (i.notification_id === n.notification_id ? { ...i, read: true } : i)));
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    }
    const link = n.meta?.link || FALLBACK_LINK[n.type] || "/app/activity";
    nav(link);
  };

  if (loading) return <FullLoader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Notifications" action={items.some((n) => !n.read) && (
        <button data-testid="mark-all-read-btn" onClick={markAll} className="text-sm font-semibold text-primary flex items-center gap-1"><CheckCheck size={16} /> Read all</button>
      )} />
      {items.length === 0 ? (
        <EmptyState icon={Bell} title="No notifications" body="Activity and security alerts will show here." />
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.notification_id} data-testid="notification-item" onClick={() => openNotification(n)}
                className={`w-full text-left flex gap-3 p-4 rounded-2xl border transition-all duration-200 active:scale-[0.99] hover:border-primary/40 ${n.read ? "bg-card border-border" : "bg-primary/5 border-primary/15"}`}>
                <span className="grid place-items-center w-10 h-10 rounded-full bg-secondary text-primary shrink-0"><Icon size={18} /></span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{n.title}</div>
                  <div className="text-sm text-muted-foreground">{n.body}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-center">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary" />}
                  <ChevronRight size={16} className="text-muted-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
